import type { Todo } from '@/api'

// Client for the backend's `/api/ws` (routers/realtime.py): authenticates with the Mini App JWT
// as its first message, keeps per-calendar subscriptions reference-counted, and reconnects with
// backoff -- re-sending auth and every live subscription each time.

type TodosListener = (calendarId: number, todos: Todo[]) => void

const MAX_BACKOFF_MS = 30_000

export class TodoSocket {
  private socket: WebSocket | null = null
  private ready = false
  private subscriptions = new Map<number, number>()
  private backoffMs = 1000
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private closedByUs = false

  constructor(
    private readonly getToken: () => string | null,
    private readonly onTodos: TodosListener,
  ) {}

  subscribe(calendarId: number): void {
    const count = this.subscriptions.get(calendarId) ?? 0
    this.subscriptions.set(calendarId, count + 1)
    if (count === 0) this.send({ type: 'subscribe', calendar_id: calendarId })
    this.ensureConnected()
  }

  unsubscribe(calendarId: number): void {
    const count = this.subscriptions.get(calendarId) ?? 0
    if (count <= 1) {
      this.subscriptions.delete(calendarId)
      this.send({ type: 'unsubscribe', calendar_id: calendarId })
    } else {
      this.subscriptions.set(calendarId, count - 1)
    }
  }

  close(): void {
    this.closedByUs = true
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.socket?.close()
    this.socket = null
  }

  private send(message: object): void {
    if (this.ready && this.socket?.readyState === WebSocket.OPEN)
      this.socket.send(JSON.stringify(message))
  }

  private ensureConnected(): void {
    if (this.socket || this.reconnectTimer) return
    const token = this.getToken()
    if (!token) return
    this.closedByUs = false
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const socket = new WebSocket(`${protocol}://${window.location.host}/api/ws`)
    this.socket = socket

    socket.addEventListener('open', () => socket.send(JSON.stringify({ type: 'auth', token })))
    socket.addEventListener('message', (event) => {
      let message: { type?: string; calendar_id?: number; todos?: Todo[] }
      try {
        message = JSON.parse(event.data as string)
      } catch {
        return
      }
      if (message.type === 'ready') {
        this.ready = true
        this.backoffMs = 1000
        for (const calendarId of this.subscriptions.keys())
          this.send({ type: 'subscribe', calendar_id: calendarId })
      } else if (
        message.type === 'todos' &&
        typeof message.calendar_id === 'number' &&
        Array.isArray(message.todos)
      ) {
        this.onTodos(message.calendar_id, message.todos)
      }
    })
    socket.addEventListener('close', () => {
      this.socket = null
      this.ready = false
      if (this.closedByUs || this.subscriptions.size === 0) return
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null
        this.ensureConnected()
      }, this.backoffMs)
      this.backoffMs = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS)
    })
  }
}
